import React, { useState, useEffect, useRef } from 'react';
import { Factory, Building, Truck, Award } from 'lucide-react';

const CompanyFoundationTimeline = () => {
  const [visibleItems, setVisibleItems] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(3);
  const timelineRef = useRef(null);

  const timelineData = [
    {
      id: 1,
      date: "1976-01-01",
      title: "Inicio de operaciones con productos de origen nacional.",
      description: "Inicio de operaciones con productos de origen nacional.",
      icon: Factory,
      color: "from-orange-400 to-orange-500"
    },
    {
      id: 2,
      date: "1983-01-01",
      title: "La oferta de productos se complementa con productos de importación.",
      description: "La oferta de productos se complementa con productos de importación.",
      icon: Truck,
      color: "from-amber-400 to-orange-500"
    },
    {
      id: 3,
      date: "1986-01-01",
      title: "Primer contenedor de canela.",
      description: "Primer contenedor de canela.",
      icon: Award,
      color: "from-orange-400 to-yellow-500"
    },
    {
      id: 4,
      date: "2002-01-01",
      title: "Instalaciones de Ciudad industrial",
      description: "Instalaciones de Ciudad industrial",
      icon: Building,
      color: "from-blue-400 to-blue-600"
    },
    {
      id: 5,
      date: "2004-01-01",
      title: "Inauguración Planta de Avena.",
      description: "Inauguración Planta de Avena.",
      icon: Factory,
      color: "from-yellow-400 to-yellow-600"
    },
    {
      id: 6,
      date: "2006-01-01",
      title: "Inauguración Sucursal Guadalajara",
      description: "Inauguración Sucursal Guadalajara",
      icon: Truck,
      color: "from-purple-400 to-purple-600"
    },
      {
        id: 7,
        date: "2012-01-01",
        title: "Inauguración Planta Campo Fresco.",
        description: "Inauguración Planta Campo Fresco.",
        icon: Factory,
        color: "from-orange-400 to-orange-700"
      },
  ];

  // Detectar el tamaño de pantalla
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      
      setIsMobile(mobile);
      
      if (mobile) {
        setItemsPerView(1);
      } else if (tablet) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: [0.1, 0.2, 0.3],
        rootMargin: '-20px 0px'
      }
    );

    const refCurrent = timelineRef.current;
    if (refCurrent) {
      observer.observe(refCurrent);
    }

    return () => {
      if (refCurrent) {
        observer.unobserve(refCurrent);
      }
    };
  }, []);

  // Animación progresiva
  useEffect(() => {
    let timer;
    
    if (isVisible && visibleItems < timelineData.length) {
      timer = setTimeout(() => {
        setVisibleItems(prev => prev + 1);
      }, isMobile ? 3000 : 2000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isVisible, visibleItems, timelineData.length, isMobile]);

  // Auto-scroll cuando aparecen más elementos que los visibles
  useEffect(() => {
    if (visibleItems > itemsPerView && isPlaying) {
      const maxIndex = Math.max(0, timelineData.length - itemsPerView);
      const targetIndex = Math.min(visibleItems - itemsPerView, maxIndex);
      setCurrentIndex(targetIndex);
    }
  }, [visibleItems, isPlaying, itemsPerView, timelineData.length]);

  // Auto-scroll continuo después de mostrar todos los elementos
  useEffect(() => {
    let timer;
    
    if (isPlaying && visibleItems === timelineData.length) {
      const maxIndex = Math.max(0, timelineData.length - itemsPerView);
      
      timer = setInterval(() => {
        setCurrentIndex(prev => {
          return (prev + 1) % (maxIndex + 1);
        });
      }, isMobile ? 5000 : 3000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isPlaying, visibleItems, timelineData.length, itemsPerView, isMobile]);

  const resetAnimation = () => {
    setVisibleItems(0);
    setIsVisible(false);
    setCurrentIndex(0);
    setIsPlaying(true);
    
    setTimeout(() => {
      setIsVisible(true);
    }, 300);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const goToSlide = (index) => {
    const maxIndex = Math.max(0, timelineData.length - itemsPerView);
    setCurrentIndex(Math.min(index, maxIndex));
    setIsPlaying(false);
  };

  // Calcular el desplazamiento basado en items por vista
  const translateX = -(currentIndex * (100 / itemsPerView));
  const maxSlides = Math.max(0, timelineData.length - itemsPerView + 1);

  return (
    <div className="min-h-screen bg-orange-50 p-4 sm:p-6 lg:p-8">
      <div ref={timelineRef} className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 bg-gradient-to-r from-orange-600 to-orange-800 bg-clip-text text-transparent">
            Historia de Almacenes Vaca
          </h1>
          <p className="text-gray-700 text-base sm:text-lg lg:text-xl mb-6 lg:mb-8 max-w-3xl mx-auto leading-relaxed px-4">
            Más de 40 años de trayectoria en la producción y procesamiento de granos de alta calidad
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <button
              onClick={resetAnimation}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-600 to-orange-600 hover:from-orange-700 hover:to-orange-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-orange-500/25"
            >
              ▶ Reproducir Historia
            </button>
            
            {visibleItems >= itemsPerView && (
              <button
                onClick={togglePlayPause}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                {isPlaying ? '⏸ Pausar' : '▶ Continuar'}
              </button>
            )}
          </div>
        </div>

        {/* Timeline Container */}
        <div className="relative mb-12 lg:mb-16">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-1000 ease-in-out"
              style={{ transform: `translateX(${translateX}%)` }}
            >
              {timelineData.map((item, index) => {
                const IconComponent = item.icon;
                const isItemVisible = index < visibleItems;
                
                return (
                  <div
                  key={item.id}
                  className={`${
                    itemsPerView === 1 ? 'w-full' : 
                    itemsPerView === 2 ? 'w-1/2' : 'w-1/3'
                  } flex-shrink-0 px-2 sm:px-4 transition-all duration-1000 ${
                    isItemVisible 
                    ? 'opacity-100 transform translate-y-0' 
                    : 'opacity-0 transform translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                  >
                  <div className={`bg-white border-2 rounded-2xl p-4 sm:p-6 lg:p-8 h-full transition-all duration-300 shadow-md relative overflow-hidden ${
                    item.highlight 
                    ? 'border-orange-300 hover:border-orange-500'
                    : 'border-orange-300 hover:border-orange-500'
                  } hover:shadow-xl`}>
                    
                    {/* Badge especial para hitos importantes */}
                    {item.highlight && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-black text-xs font-bold px-3 py-1 rounded-full">
                      HITO CLAVE
                    </div>
                    )}
                    
                    {item.future && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-cyan-400 to-blue-400 text-black text-xs font-bold px-3 py-1 rounded-full">
                      PRÓXIMAMENTE
                    </div>
                    )}

                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                    <div className={`p-3 lg:p-4 rounded-xl bg-gradient-to-r ${item.color} shadow-lg`}>
                      <IconComponent className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-gray-700 text-xs sm:text-sm font-medium mb-1">
                      {item.future ? 'Próximamente' : new Date(item.date).getFullYear()}
                      </div>
                      <div className="text-gray-600 text-xs">
                      📍 {item.location}
                      </div>
                    </div>
                    </div>
                    
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-3 lg:mb-4 leading-tight">
                    {item.title}
                    </h3>
                    
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    {item.description}
                    </p>
                  </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Indicadores de navegación */}
        {visibleItems >= itemsPerView && maxSlides > 1 && (
          <div className="flex justify-center gap-2 sm:gap-3 mb-8 lg:mb-12 flex-wrap">
            {Array.from({ length: maxSlides }, (_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
                  currentIndex === index
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg scale-125'
                    : 'bg-gray-400 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        )}

        {/* Barra de progreso */}
        <div className="mt-8 lg:mt-12 mb-8 lg:mb-12">
          <div className="bg-orange-200 rounded-full h-2 sm:h-3 mb-4 lg:mb-6 overflow-hidden shadow-inner">
            <div 
              className="bg-gradient-to-r from-orange-600 via-orange-600 to-orange-700 h-full rounded-full transition-all duration-1000 ease-out relative shadow-sm"
              style={{ width: `${(visibleItems / timelineData.length) * 100}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full"></div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-gray-800 text-base sm:text-lg font-medium">
              Hitos Revelados: <span className="text-orange-800 font-bold">{visibleItems}</span> de <span className="text-orange-800 font-bold">{timelineData.length}</span>
            </p>
            <p className="text-gray-700 mt-2 text-sm sm:text-base">
              {Math.round((visibleItems / timelineData.length) * 100)}% de la historia completada
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default CompanyFoundationTimeline;